import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit,
  getCountFromServer, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { PageHeader, StatCard, Card, Badge, Spinner } from '../components/UI';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar,
  AreaChart, Area } from 'recharts';

const ACCENT = '#008751';
const GOLD = '#D4AF37';

const TIER_COLORS = {
  free:'#6b7280', student:'#008751', premium:'#3b82f6',
  scholar:'#D4AF37', family:'#a855f7',
};

const CHART_TOOLTIP = {
  contentStyle:{background:'#1A2332',border:'1px solid rgba(0,135,81,0.2)',
    borderRadius:10,fontSize:12,boxShadow:'0 8px 24px rgba(0,0,0,0.3)'},
  itemStyle:{color:'#fff'},
  labelStyle:{color:'rgba(255,255,255,0.5)',marginBottom:4},
};

export default function Dashboard() {
  const [stats,    setStats]    = useState(null);
  const [alerts,   setAlerts]   = useState([]);
  const [tierData, setTierData] = useState([]);
  const [dau,      setDau]      = useState([]);
  const [revenue,  setRevenue]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    try {
      const usersCol = collection(db, 'users');

      // Total users — simple count, no date filter
      const totalSnap = await getCountFromServer(usersCol);
      const totalUsers = totalSnap.data().count;

      // Tier distribution
      const tiers = ['free','student','premium','scholar','family'];
      const tierCounts = await Promise.all(tiers.map(t =>
        getCountFromServer(query(usersCol, where('subscriptionTier','==',t)))
          .then(s => s.data().count).catch(() => 0)
      ));
      const tierArr = tiers.map((t,i) => ({
        name: t.charAt(0).toUpperCase()+t.slice(1),
        value: tierCounts[i],
        color: TIER_COLORS[t],
      })).filter(d => d.value > 0);
      setTierData(tierArr);

      // New users today and this week — safe with try/catch
      let newToday = 0, newThisWeek = 0, activeToday = 0;
      try {
        const today   = new Date(); today.setHours(0,0,0,0);
        const weekAgo = new Date(Date.now() - 7*24*60*60*1000);
        const [ts, ws] = await Promise.all([
          getCountFromServer(query(usersCol,
            where('createdAt','>=',Timestamp.fromDate(today)))),
          getCountFromServer(query(usersCol,
            where('createdAt','>=',Timestamp.fromDate(weekAgo)))),
        ]);
        newToday    = ts.data().count;
        newThisWeek = ws.data().count;
      } catch(e) { console.log('Date query skipped:', e.message); }

      // Active today
      try {
        const today = new Date(); today.setHours(0,0,0,0);
        const activeSnap = await getCountFromServer(query(usersCol,
          where('lastActive','>=',Timestamp.fromDate(today))));
        activeToday = activeSnap.data().count;
      } catch(e) { console.log('Active today skipped:', e.message); }

      // Revenue from receipts — safe
      let monthRevenue = 0, todayRevenue = 0, totalSubs = 0;
      try {
        const today    = new Date(); today.setHours(0,0,0,0);
        const monthAgo = new Date(Date.now() - 30*24*60*60*1000);
        const receiptsSnap = await getDocs(query(
          collection(db,'receipts'),
          where('paidAt','>=',Timestamp.fromDate(monthAgo))
        ));
        receiptsSnap.forEach(d => {
          const data = d.data();
          const amt  = (data.amount || 0) / 100;
          monthRevenue += amt;
          totalSubs++;
          if (data.paidAt?.toDate?.() >= today) todayRevenue += amt;
        });
      } catch(e) { console.log('Revenue query skipped:', e.message); }

      // Security alerts — safe
      let openAlerts = 0;
      const alertsList = [];
      try {
        const alertsSnap = await getDocs(query(
          collection(db,'security_alerts'),
          where('status','==','open'),
          orderBy('createdAt','desc'),
          limit(5)
        ));
        openAlerts = alertsSnap.size;
        alertsSnap.docs.forEach(d => alertsList.push({id:d.id,...d.data()}));
      } catch(e) { console.log('Alerts query skipped:', e.message); }
      setAlerts(alertsList);

      // API calls today — safe
      let apiCallsToday = 0;
      try {
        const today = new Date(); today.setHours(0,0,0,0);
        const apiSnap = await getCountFromServer(query(
          collection(db,'api_logs'),
          where('timestamp','>=',Timestamp.fromDate(today))
        ));
        apiCallsToday = apiSnap.data().count;
      } catch(e) { console.log('API logs query skipped:', e.message); }

      setStats({
        totalUsers,
        newToday,
        newThisWeek,
        activeToday,
        monthRevenue: monthRevenue.toFixed(2),
        todayRevenue: todayRevenue.toFixed(2),
        totalSubs,
        openAlerts,
        apiCallsToday,
      });

      // DAU chart (API calls per day) — safe
      const dauData = [];
      try {
        for (let i = 6; i >= 0; i--) {
          const dayStart = new Date(Date.now() - i*24*60*60*1000);
          dayStart.setHours(0,0,0,0);
          const dayEnd = new Date(dayStart.getTime() + 24*60*60*1000);
          const snap = await getCountFromServer(query(
            collection(db,'api_logs'),
            where('timestamp','>=',Timestamp.fromDate(dayStart)),
            where('timestamp','<', Timestamp.fromDate(dayEnd)),
          )).catch(() => ({data:()=>({count:0})}));
          dauData.push({
            day: dayStart.toLocaleDateString('en',{weekday:'short'}),
            messages: snap.data().count,
          });
        }
      } catch(e) { console.log('DAU query skipped:', e.message); }
      setDau(dauData);

      // Revenue per day (last 7 days) — safe
      const revData = [];
      try {
        for (let i = 6; i >= 0; i--) {
          const dayStart = new Date(Date.now() - i*24*60*60*1000);
          dayStart.setHours(0,0,0,0);
          const dayEnd = new Date(dayStart.getTime() + 24*60*60*1000);
          const snap = await getDocs(query(
            collection(db,'receipts'),
            where('paidAt','>=',Timestamp.fromDate(dayStart)),
            where('paidAt','<', Timestamp.fromDate(dayEnd)),
          )).catch(() => ({forEach:()=>{}}));
          let dayTotal = 0;
          if (snap?.forEach) snap.forEach(d => { dayTotal += (d.data().amount||0)/100; });
          revData.push({
            day: dayStart.toLocaleDateString('en',{weekday:'short'}),
            revenue: parseFloat(dayTotal.toFixed(2)),
          });
        }
      } catch(e) { console.log('Revenue chart skipped:', e.message); }
      setRevenue(revData);

    } catch(e) {
      console.error('Dashboard load error:', e);
    }
    setLoading(false);
  }

  if (loading) return <div style={{padding:32}}><Spinner /></div>;

  return (
    <div style={{padding:32}}>
      <PageHeader
        title="Dashboard"
        subtitle={`Last updated: ${new Date().toLocaleString()}`} />

      {/* Stat Cards Row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',
        gap:16,marginBottom:28}}>
        <StatCard label="Total Users" value={stats?.totalUsers || 0}
          icon="👥" color={ACCENT} borderColor={ACCENT}
          sub={`+${stats?.newThisWeek} this week`} trend={stats?.newThisWeek > 0 ? 12 : 0} />
        <StatCard label="Active Today" value={stats?.activeToday || 0}
          icon="🟢" color={ACCENT} borderColor="#22c55e" />
        <StatCard label="Messages Today" value={stats?.apiCallsToday || 0}
          icon="💬" color="#3b82f6" borderColor="#3b82f6" />
        <StatCard label="Revenue (Month)" value={`$${stats?.monthRevenue}`}
          icon="💰" color={GOLD} borderColor={GOLD}
          sub={`$${stats?.todayRevenue} today`} />
        <StatCard label="Subscriptions" value={stats?.totalSubs || 0}
          icon="⭐" color="#a855f7" borderColor="#a855f7" />
      </div>

      {/* Charts Row 1: User Growth (Line) + Messages Per Day (Bar) */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:20}}>
        <Card title="Messages Per Day (Last 7 Days)">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dau} margin={{top:5,right:5,left:-20,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" tick={{fill:'rgba(255,255,255,0.35)',fontSize:11}} axisLine={{stroke:'rgba(255,255,255,0.06)'}} />
              <YAxis tick={{fill:'rgba(255,255,255,0.35)',fontSize:11}} axisLine={{stroke:'rgba(255,255,255,0.06)'}} />
              <Tooltip {...CHART_TOOLTIP} />
              <Bar dataKey="messages" fill={ACCENT} radius={[6,6,0,0]}
                fillOpacity={0.85} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Revenue Trend (Last 7 Days)">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={revenue} margin={{top:5,right:5,left:-20,bottom:0}}>
              <defs>
                <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={GOLD} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={GOLD} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" tick={{fill:'rgba(255,255,255,0.35)',fontSize:11}} axisLine={{stroke:'rgba(255,255,255,0.06)'}} />
              <YAxis tick={{fill:'rgba(255,255,255,0.35)',fontSize:11}} axisLine={{stroke:'rgba(255,255,255,0.06)'}} />
              <Tooltip {...CHART_TOOLTIP} formatter={(v) => [`$${v}`, 'Revenue']} />
              <Area type="monotone" dataKey="revenue" stroke={GOLD}
                strokeWidth={2} fill="url(#goldGrad)" dot={{fill:GOLD,r:3}} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Charts Row 2: Tier Distribution (Pie) + User Growth (Line) */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:28}}>
        {/* Tier Donut */}
        <Card title="Tier Distribution">
          {tierData.length > 0 ? (
            <div style={{display:'flex',alignItems:'center',gap:24}}>
              <ResponsiveContainer width={180} height={180}>
                <PieChart>
                  <Pie data={tierData} innerRadius={55} outerRadius={80}
                    paddingAngle={3} dataKey="value" strokeWidth={0}>
                    {tierData.map((entry,i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip {...CHART_TOOLTIP} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{flex:1,display:'flex',flexDirection:'column',gap:10}}>
                {tierData.map((d,i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',
                    justifyContent:'space-between',gap:8,
                    padding:'8px 12px',background:'rgba(255,255,255,0.02)',
                    borderRadius:8}}>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <div style={{width:10,height:10,borderRadius:3,
                        background:d.color,flexShrink:0}} />
                      <span style={{fontSize:13,color:'rgba(255,255,255,0.6)'}}>{d.name}</span>
                    </div>
                    <span style={{fontFamily:'DM Mono',fontSize:14,
                      color:'#fff',fontWeight:600}}>{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <div style={{color:'rgba(255,255,255,0.3)',fontSize:13,padding:'20px 0'}}>No data yet</div>}
        </Card>

        {/* API Calls Line */}
        <Card title="API Calls Trend">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={dau} margin={{top:5,right:5,left:-20,bottom:0}}>
              <defs>
                <linearGradient id="greenGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={ACCENT} stopOpacity={0.2}/>
                  <stop offset="95%" stopColor={ACCENT} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" tick={{fill:'rgba(255,255,255,0.35)',fontSize:11}} axisLine={{stroke:'rgba(255,255,255,0.06)'}} />
              <YAxis tick={{fill:'rgba(255,255,255,0.35)',fontSize:11}} axisLine={{stroke:'rgba(255,255,255,0.06)'}} />
              <Tooltip {...CHART_TOOLTIP} />
              <Line type="monotone" dataKey="messages" stroke={ACCENT}
                strokeWidth={2} dot={{fill:ACCENT,r:4,strokeWidth:0}}
                activeDot={{r:6,fill:ACCENT,stroke:'#fff',strokeWidth:2}} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Recent Activity Feed */}
      <Card title="Recent Activity">
        {alerts.length > 0 ? (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {alerts.map(alert => (
              <div key={alert.id} style={{
                display:'flex',alignItems:'center',justifyContent:'space-between',
                padding:'14px 18px',background:'rgba(239,68,68,0.04)',
                border:'1px solid rgba(239,68,68,0.1)',borderRadius:12,
                transition:'background 0.2s'}}>
                <div style={{display:'flex',alignItems:'center',gap:14}}>
                  <div style={{width:38,height:38,borderRadius:10,
                    background:'rgba(239,68,68,0.1)',display:'flex',
                    alignItems:'center',justifyContent:'center',fontSize:16}}>
                    🚨
                  </div>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:'#fff'}}>
                      {alert.alertType?.replace(/_/g,' ').toUpperCase()}
                    </div>
                    <div style={{fontSize:12,color:'rgba(255,255,255,0.35)',marginTop:3}}>
                      {alert.email || alert.uid} {alert.country ? `\u2022 ${alert.country}` : ''}
                    </div>
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <Badge label="open" color="open" />
                  <a href="/safety" style={{fontSize:12,color:ACCENT,
                    textDecoration:'none',fontWeight:600,letterSpacing:0.3}}>
                    Review →
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{textAlign:'center',padding:'24px 0',color:'rgba(255,255,255,0.25)',
            fontSize:13}}>
            <div style={{fontSize:28,marginBottom:8}}>✓</div>
            No open alerts — all clear
          </div>
        )}
      </Card>
    </div>
  );
}
